import React from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, CornerDownLeft } from "lucide-react";
import api from "@/lib/api";

export default function GlobalSearch() {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const nav = useNavigate();
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); setOpen(true); }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  React.useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open]);

  React.useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get("/search", { params: { q } }); setResults(data.results || []); }
      catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (r) => { setOpen(false); setQ(""); nav(r.link); };

  const grouped = results.reduce((acc, r) => { (acc[r.type] = acc[r.type] || []).push(r); return acc; }, {});

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="global-search-trigger"
        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500 hover:border-slate-300 transition-colors w-full sm:w-72">
        <Search className="h-4 w-4" /><span className="flex-1 text-left">Search…</span>
        <kbd className="hidden sm:inline text-[10px] rounded bg-white border border-slate-200 px-1.5 py-0.5 text-slate-400">⌘K</kbd>
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-slate-900/30 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-4 border-b border-slate-100">
              <Search className="h-4 w-4 text-slate-400" />
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} data-testid="global-search-input"
                placeholder="Search partners, requirements, RFQs, candidates, invoices…"
                className="flex-1 py-4 text-sm focus:outline-none" />
              {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-2">
              {q.length >= 2 && !loading && results.length === 0 && (
                <div className="py-10 text-center text-sm text-slate-400">No results for "{q}"</div>
              )}
              {Object.entries(grouped).map(([type, items]) => (
                <div key={type} className="mb-2">
                  <div className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-400">{type}</div>
                  {items.map((r) => (
                    <button key={r.id} onClick={() => go(r)} data-testid={`search-result-${r.id}`}
                      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-slate-50 text-left group">
                      <div><div className="text-sm font-medium text-slate-800">{r.title}</div>
                        {r.subtitle && <div className="text-xs text-slate-400 capitalize">{r.subtitle}</div>}</div>
                      <CornerDownLeft className="h-3.5 w-3.5 text-slate-300 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                </div>
              ))}
              {q.length < 2 && <div className="py-10 text-center text-sm text-slate-400">Type at least 2 characters to search</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
